import { TokenAdmin, TenantId } from '@/store/util';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { useEffect, useRef, useState, useCallback } from 'react';

export interface EdgeLinkLogEntry {
  timestampUnixMs: number;
  level: 'Unspecified' | 'Debug' | 'Info' | 'Warn' | 'Error';
  message: string;
  source: string;
}

export interface EdgeLinkLogReceived {
  bridgeId: string;
  entries: EdgeLinkLogEntry[];
  clientVersion: string;
}

interface UseEdgeLinkLogsOptions {
  bridgeId: string | undefined;
  enabled?: boolean;
  maxLogs?: number; // Cap in-memory logs (default 200)
}

type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'disconnecting';

export function useEdgeLinkLogs({
  bridgeId,
  enabled = true,
  maxLogs = 200,
}: UseEdgeLinkLogsOptions) {
  const [logs, setLogs] = useState<EdgeLinkLogEntry[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const connectionRef = useRef<HubConnection | null>(null);
  const connectionStateRef = useRef<ConnectionState>('disconnected');
  const previousBridgeIdRef = useRef<string | undefined>(bridgeId);

  // Handle incoming logs - append new, trim old
  const handleLogReceived = useCallback(
    (data: EdgeLinkLogReceived) => {
      setLogs((prev) => {
        const combined = [...prev, ...data.entries];
        // Keep only the most recent logs
        return combined.slice(-maxLogs);
      });
    },
    [maxLogs]
  );

  // Cleanup function
  const cleanupConnection = useCallback(async () => {
    if (!connectionRef.current || connectionStateRef.current === 'disconnecting') {
      return;
    }

    connectionStateRef.current = 'disconnecting';

    try {
      if (previousBridgeIdRef.current && connectionRef.current.state === 'Connected') {
        await connectionRef.current.invoke(
          'UnsubscribeFromBridgeLogs',
          previousBridgeIdRef.current
        );
      }
      await connectionRef.current.stop();
      connectionRef.current = null;
      connectionStateRef.current = 'disconnected';
      setIsConnected(false);
    } catch (err) {
      console.error('Error cleaning up EdgeLink SignalR connection:', err);
      connectionStateRef.current = 'disconnected';
      setIsConnected(false);
    }
  }, []);

  useEffect(() => {
    if (!bridgeId || !enabled) {
      // Clean up if disabled or no bridgeId
      if (connectionRef.current) {
        cleanupConnection();
      }
      return;
    }

    const setupConnection = async () => {
      // Don't start a new connection if we're in the process of connecting or disconnecting
      if (['connecting', 'disconnecting'].includes(connectionStateRef.current)) {
        return;
      }

      // If we already have a connected connection and the bridgeId hasn't changed, keep it
      if (
        connectionRef.current?.state === 'Connected' &&
        previousBridgeIdRef.current === bridgeId
      ) {
        return;
      }

      // Clean up any existing connection
      await cleanupConnection();

      // Create new connection
      if (connectionStateRef.current === 'disconnected') {
        connectionStateRef.current = 'connecting';

        // Use VITE_ADMIN_API if set (local dev), otherwise construct from VITE_BASE_URL
        const baseUrl =
          import.meta.env.VITE_ADMIN_API ||
          `https://api.admin.${import.meta.env.VITE_BASE_URL}`;

        const tenantId = TenantId.get();
        // Build URL with tenantId query param (WebSocket connections can't send headers)
        const hubUrl = new URL(`${baseUrl}/realtime/edgelink`);
        if (tenantId) {
          hubUrl.searchParams.set('tenantId', tenantId);
        }

        const connection = new HubConnectionBuilder()
          .withUrl(hubUrl.toString(), {
            accessTokenFactory: () => TokenAdmin.get() || '',
            // Headers are sent for HTTP requests (negotiate, long polling)
            // Query string tenantId handles the WebSocket upgrade which can't send headers
            headers: tenantId ? { 'X-Tenant-Id': tenantId } : undefined,
          })
          .configureLogging(LogLevel.Warning)
          .withAutomaticReconnect({
            nextRetryDelayInMilliseconds: (retryContext) => {
              if (retryContext.previousRetryCount === 0) return 0;
              if (retryContext.elapsedMilliseconds < 60000) {
                return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 5000);
              }
              return null;
            },
          })
          .build();

        // Set up connection state change handlers
        connection.onclose(() => {
          connectionStateRef.current = 'disconnected';
          setIsConnected(false);
        });

        connection.onreconnecting(() => {
          connectionStateRef.current = 'connecting';
          setIsConnected(false);
        });

        connection.onreconnected(async () => {
          connectionStateRef.current = 'connected';
          setIsConnected(true);
          // Re-subscribe after reconnection
          if (bridgeId) {
            try {
              await connection.invoke('SubscribeToBridgeLogs', bridgeId);
              setError(null);
            } catch (err) {
              const message =
                err instanceof Error ? err.message : 'Failed to subscribe to bridge logs';
              // Extract HubException message if present
              const hubMatch = message.match(/HubException: (.+)/);
              setError(hubMatch ? hubMatch[1] : message);
            }
          }
        });

        connection.on('EdgeLinkLogReceived', handleLogReceived);

        connectionRef.current = connection;

        try {
          await connection.start();
          connectionStateRef.current = 'connected';

          // Subscribe to bridge logs
          await connection.invoke('SubscribeToBridgeLogs', bridgeId);
          previousBridgeIdRef.current = bridgeId;
          setIsConnected(true);
          setError(null);
        } catch (err) {
          console.error('EdgeLink logs connection failed:', err);
          const message =
            err instanceof Error ? err.message : 'Failed to connect to bridge logs';
          // Extract HubException message if present
          const hubMatch = message.match(/HubException: (.+)/);
          setError(hubMatch ? hubMatch[1] : message);
          connectionRef.current = null;
          connectionStateRef.current = 'disconnected';
          setIsConnected(false);
        }
      }
    };

    setupConnection();

    return () => {
      cleanupConnection();
    };
  }, [bridgeId, enabled, cleanupConnection, handleLogReceived]);

  const clearLogs = useCallback(() => setLogs([]), []);
  const clearError = useCallback(() => setError(null), []);

  return { logs, isConnected, error, clearLogs, clearError };
}
